import React from "react";
import { SourceInfo } from "../types";

interface CitationProps {
  number: number;
  source: SourceInfo;
  onClick: (url: string) => void;
}

// Pattern for citation markers like [sop-ca93c5]: lowercase prefix, hyphen,
// alphanumeric id. Used to remove markers from the raw streaming HTML and to
// strip leftover markers in chunks that don't have a matching sources entry,
// so users never see the raw indicator text.
export const CITATION_MARKER_RE = /\[[a-z]+-[a-z0-9]+\]/gi;

export const stripCitationMarkers = (html: string): string =>
  html.replace(CITATION_MARKER_RE, "");

// Source urls from the citation feed are Frontify-relative paths like
// "/document/112/show/<asset-jwt>:reverb-hotels:<sig>". They work as-is
// inside the Frontify-embedded widget (the parent frame resolves them
// against its own *.frontify.com origin), but window.open from the
// standalone chat would route them to our own domain. Expand to an
// absolute Frontify URL by pulling the workspace slug out of the token.
//
// Already-absolute urls and unrecognised relative paths are returned as-is.
const FRONTIFY_WORKSPACE_IN_URL_RE = /:([a-z0-9-]+):/i;
export const resolveSourceUrl = (url: string): string => {
  if (/^https?:\/\//i.test(url)) return url;
  const match = url.match(FRONTIFY_WORKSPACE_IN_URL_RE);
  if (match) {
    return `https://${match[1]}.frontify.com${url}`;
  }
  return url;
};

const escapeRegex = (s: string) => s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

// Walks the message html and assigns each cited marker a 1-based number in
// first-occurrence order. Numbering is deduped by source url, not by marker
// — chunks of the same page show up as different markers (each chunk gets
// its own chunk_id) but resolve to the same destination, so they should
// share a number. The first SourceInfo encountered for a url wins for the
// footer display.
//
// Returns:
//   markerToNumber — every marker in the html mapped to its chip number
//                    (multiple markers may map to the same number)
//   numberToSource — one entry per unique url, used by the footer to render
//                    one row per real destination
export const computeCitationNumbers = (
  html: string,
  sources: Record<string, SourceInfo> | undefined,
): {
  markerToNumber: Map<string, number>;
  numberToSource: Map<number, SourceInfo>;
} => {
  const markerToNumber = new Map<string, number>();
  const numberToSource = new Map<number, SourceInfo>();
  if (!sources) return { markerToNumber, numberToSource };
  const markers = Object.keys(sources);
  if (markers.length === 0) return { markerToNumber, numberToSource };

  const pattern = new RegExp(markers.map(escapeRegex).join("|"), "g");
  const urlToNumber = new Map<string, number>();
  let nextNumber = 1;

  for (const match of html.matchAll(pattern)) {
    const marker = match[0];
    if (markerToNumber.has(marker)) continue;
    const source = sources[marker];
    if (!source) continue;

    const existing = urlToNumber.get(source.url);
    if (existing !== undefined) {
      markerToNumber.set(marker, existing);
    } else {
      const number = nextNumber++;
      urlToNumber.set(source.url, number);
      numberToSource.set(number, source);
      markerToNumber.set(marker, number);
    }
  }

  return { markerToNumber, numberToSource };
};

// Wikipedia-style superscript [N] rendered in place of a citation marker.
// Tooltip carries the source's page title (and section heading if it
// differs); click invokes the parent-supplied handler — Chat.tsx decides
// between window.open in chat mode and a parent-frame postMessage in widget
// mode. The footer below the message keeps the filled-chip styling for
// scannability.
export const Citation: React.FC<CitationProps> = ({
  number,
  source,
  onClick,
}) => {
  const tooltip =
    source.page_title === source.section_heading
      ? source.page_title
      : `${source.page_title} — ${source.section_heading}`;

  return (
    <button
      type="button"
      title={tooltip}
      onClick={(e) => {
        e.preventDefault();
        e.stopPropagation();
        onClick(source.url);
      }}
      className="text-[10px] font-semibold text-primary hover:underline cursor-pointer align-super px-0.5 leading-none"
    >
      [{number}]
    </button>
  );
};

// Splits a formatted-HTML message into a React tree where citation markers
// (the keys of the sources map) become numbered Citation chips. Surrounding
// HTML chunks are rendered via dangerouslySetInnerHTML so existing markdown
// formatting still applies — but with any leftover marker patterns stripped
// out so users never see raw [sop-xxx] text.
//
// Returns null when there are no markers to replace OR no sources, so
// callers can fall back to a plain (still marker-stripped) render path.
export const renderWithCitations = (
  html: string,
  sources: Record<string, SourceInfo> | undefined,
  onClick: (url: string) => void,
): React.ReactNode | null => {
  if (!sources) return null;
  const { markerToNumber } = computeCitationNumbers(html, sources);
  if (markerToNumber.size === 0) return null;

  const markers = Object.keys(sources);
  const pattern = new RegExp(markers.map(escapeRegex).join("|"), "g");
  const matches = [...html.matchAll(pattern)];

  const parts: React.ReactNode[] = [];
  let lastIndex = 0;
  let key = 0;

  for (const match of matches) {
    const start = match.index ?? 0;
    if (start > lastIndex) {
      parts.push(
        <span
          key={key++}
          dangerouslySetInnerHTML={{
            __html: stripCitationMarkers(html.slice(lastIndex, start)),
          }}
        />,
      );
    }
    const marker = match[0];
    parts.push(
      <Citation
        key={key++}
        number={markerToNumber.get(marker)!}
        source={sources[marker]}
        onClick={onClick}
      />,
    );
    lastIndex = start + marker.length;
  }

  if (lastIndex < html.length) {
    parts.push(
      <span
        key={key++}
        dangerouslySetInnerHTML={{
          __html: stripCitationMarkers(html.slice(lastIndex)),
        }}
      />,
    );
  }

  return <>{parts}</>;
};

interface SourcesFooterProps {
  html: string;
  sources: Record<string, SourceInfo> | undefined;
  onClick: (url: string) => void;
}

// Renders a compact "Sources" list below a bot message: the same numbered
// chips as the inline citations, paired with the page title (and section
// heading when distinct). Each row is clickable and dispatches through the
// same onClick handler as the inline chips.
export const SourcesFooter: React.FC<SourcesFooterProps> = ({
  html,
  sources,
  onClick,
}) => {
  if (!sources) return null;
  const { numberToSource } = computeCitationNumbers(html, sources);
  if (numberToSource.size === 0) return null;

  const entries = [...numberToSource.entries()].sort((a, b) => a[0] - b[0]);

  return (
    <div className="mt-3 pt-2 border-t border-gray-200">
      <p className="text-[10px] font-semibold text-gray-500 uppercase tracking-wide mb-1">
        Sources
      </p>
      <ul className="space-y-1">
        {entries.map(([number, source]) => (
          <li key={number} className="text-xs">
            <button
              type="button"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                onClick(source.url);
              }}
              className="text-left flex items-start gap-1.5 hover:underline cursor-pointer w-full"
            >
              <span className="inline-flex items-center justify-center min-w-[1.25rem] h-[1.1rem] text-[10px] font-semibold px-1 rounded bg-primary text-white shrink-0 leading-none mt-px">
                {number}
              </span>
              <span className="text-gray-700">
                {source.page_title}
                {source.section_heading &&
                  source.section_heading !== source.page_title && (
                    <span className="text-gray-500">
                      {" — "}
                      {source.section_heading}
                    </span>
                  )}
              </span>
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
};
